import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { io } from 'socket.io-client';
import axiosInstance from '../services/axiosInstance';
import { UserContext } from './userContext';

const DEFAULT_ANNOUNCEMENT = 'No hay anuncios en este momento.';

const AnnouncementContext = createContext({
  announcement: DEFAULT_ANNOUNCEMENT,
  loading: false,
  refreshAnnouncement: async () => {},
  updateAnnouncement: () => {},
});

export const AnnouncementProvider = ({ children }) => {
  const [announcement, setAnnouncement] = useState(DEFAULT_ANNOUNCEMENT);
  const [loading, setLoading] = useState(false);
  const { user } = useContext(UserContext);

  const refreshAnnouncement = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/users/announcement/get');
      const latest =
        Array.isArray(response.data) && response.data.length > 0
          ? response.data[0].content
          : DEFAULT_ANNOUNCEMENT;

      setAnnouncement(latest || DEFAULT_ANNOUNCEMENT);
    } catch (error) {
      console.error('Error fetching announcements:', error.message);
      setAnnouncement(DEFAULT_ANNOUNCEMENT);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAnnouncement = useCallback(newContent => {
    setAnnouncement(newContent || DEFAULT_ANNOUNCEMENT);
  }, []);

  // Se re-ejecuta en el mount inicial Y cada vez que el usuario inicia/cierra sesión
  const userId = user?.id;
  useEffect(() => {
    refreshAnnouncement();
  }, [userId, refreshAnnouncement]);

  // Escuchar eventos socket para actualizar el anuncio en tiempo real
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SERVER_URL);

    socket.on('announcementUpdated', ({ content }) => {
      setAnnouncement(content || DEFAULT_ANNOUNCEMENT);
    });

    return () => {
      socket.off('announcementUpdated');
      socket.disconnect();
    };
  }, []);

  return (
    <AnnouncementContext.Provider
      value={{ announcement, loading, refreshAnnouncement, updateAnnouncement }}
    >
      {children}
    </AnnouncementContext.Provider>
  );
};

export const useAnnouncement = () => useContext(AnnouncementContext);
